import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GenerateReportRequest } from 'features/lead-management/types/request';

import { IdName, SelectedItemsObj } from '@components/multi-selector/types';
import { TimeZones } from '@constants/date';
import {
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { SUBSCRIPTIONS } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import {
  addDownload,
  updateDownloadStatus
} from '@features/downloads/downloadsSlice';
import { formatDateRange } from '@features/group-management/helper';
import { TRIAL_MOCK_IDS } from '@features/trial-mode/constants';
import useNotify from '@hooks/notify';
import { ApiError, DateRange } from '@types';
import { getFormattedDateTimeWithOffset } from '@utils/date';
import { downloadFileFromUrlUsingBlob } from '@utils/downloadFile';
import usePollDownloadStatus from '@utils/pollDownloadStatus';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';

import {
  useGenerateLeadReportMutation,
  useGetCustomReportJobIdV3Mutation,
  useLazyGetChargesReportJobIdQuery,
  useLazyGetGstReportJobIdQuery
} from '../queries/ReportsQuery';

export const buildCustomReportV3Body = ({
  fromTimestamp,
  toTimestamp,
  selectedBranchIds = [],
  selectedGroups = {},
  allGroupsByBranch = {},
  includeMarkAsPaid,
  includeQuickCollect,
  includeTaxInfo = false,
  category = '',
  paymentMode = '',
  customFields = [],
  globalFields = [],
  reportType
}: {
  fromTimestamp: string;
  toTimestamp: string;
  selectedBranchIds?: string[];
  selectedGroups?: SelectedItemsObj;
  allGroupsByBranch?: SelectedItemsObj;
  includeMarkAsPaid: boolean;
  includeQuickCollect: boolean;
  includeTaxInfo?: boolean;
  category?: string;
  paymentMode?: string;
  customFields?: string[];
  globalFields?: string[];
  reportType: 'branch' | 'organisation';
}) => {
  const buildBranchPayload = (branchId: string, groups: IdName[]) => {
    if (!groups.length) {
      return { branch_id: branchId, group_ids: [] };
    }

    const allBranchGroups = allGroupsByBranch[branchId] ?? [];
    const isAllGroupsSelected =
      allBranchGroups.length > 0 &&
      groups.length === allBranchGroups.length &&
      allBranchGroups.every((group) =>
        groups.some((selectedGroup) => selectedGroup.id === group.id)
      );

    if (isAllGroupsSelected) {
      return { branch_id: branchId, group_ids: [] };
    }

    return {
      branch_id: branchId,
      group_ids: groups.map((group) => group.id)
    };
  };

  const branches = selectedBranchIds.length
    ? selectedBranchIds.map((branchId) =>
        buildBranchPayload(branchId, selectedGroups[branchId] ?? [])
      )
    : Object.entries(selectedGroups).map(([branchId, groups]) =>
        buildBranchPayload(branchId, groups)
      );

  return {
    from_timestamp: fromTimestamp,
    to_timestamp: toTimestamp,
    branches,
    include_mark_as_paid: includeMarkAsPaid,
    include_quick_collect: includeQuickCollect,
    include_tax_info: includeTaxInfo,
    category,
    payment_mode: paymentMode,
    custom_fields: customFields,
    global_fields: globalFields,
    report_type: reportType
  };
};

const useDownloadReport = () => {
  const { createErrorAlert } = useNotify();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pollDownloadStatus, pollLeadReportJobStatus } =
    usePollDownloadStatus();
  const [getGstReportJobId] = useLazyGetGstReportJobIdQuery();
  const [getCustomReportJobIdV3] = useGetCustomReportJobIdV3Mutation();
  const [getChargesReportJobId] = useLazyGetChargesReportJobIdQuery();
  const [getReportJobStatus] = useGenerateLeadReportMutation();

  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const { currentSubscription } = useSelector((state: any) => state.user);
  const isTrialMode = useSelector((state: any) => state.trial?.isTrialMode);
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );

  const formattedDate = (date: string | Date, isEndDate = false) =>
    getFormattedDateTimeWithOffset(date, TimeZones['Asia/Kolkata'], isEndDate);

  const handleDownloadGstReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    isMarkAsPaidChecked: boolean,
    branchNames: string[] = [],
    globalFields: string[] = [],
    customFields: string[] = []
  ) => {
    const { startDate, endDate } = formatDateRange(selectedDateRange);

    if (isTrialMode) {
      const downloadId = `gst-report-${Date.now()}`;
      dispatch(
        addDownload({
          id: downloadId,
          fileName: downloadId,
          fileType: 'xlsx'
        })
      );
      try {
        const { generateTrialGstReport } = await import(
          '@features/trial-mode/mock-data/reports'
        );
        const blob = await generateTrialGstReport(
          TRIAL_MOCK_IDS.ORG_NAME,
          startDate,
          endDate
        );
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(now.getDate()).padStart(2, '0')}_${String(
          now.getHours()
        ).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(
          now.getSeconds()
        ).padStart(2, '0')}`;
        await downloadFileFromUrlUsingBlob(url, `GST_Report_${ts}.xlsx`);
        URL.revokeObjectURL(url);
        dispatch(updateDownloadStatus({ id: downloadId, status: 'completed' }));
      } catch {
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'error',
            error: 'Failed to generate report'
          })
        );
      }
      return;
    }

    const queryObject = {
      from_timestamp: formattedDate(startDate),
      to_timestamp: formattedDate(endDate, true),
      branch_id: selectedBranchIds.join(','),
      payment_mode: isMarkAsPaidChecked ? 'all' : 'automatic',
      ...(globalFields.length ? { global_fields: globalFields.join(',') } : {}),
      ...(customFields.length ? { custom_fields: customFields.join(',') } : {})
    };
    const queryString = buildQueryString(queryObject);

    const downloadId = `gst-report-${Date.now()}`;

    const res = await getGstReportJobId({
      params: {
        organizationId,
        branchId: defaultBranchId
      },
      queryString
    });

    if (res?.data) {
      const jobId = res.data?.job_id;
      if (jobId) {
        dispatch(
          addDownload({
            id: downloadId,
            fileName: downloadId,
            fileType: 'xlsx'
          })
        );
        const fileType = 'gst_report';

        await pollDownloadStatus({
          jobId,
          downloadId,
          mixpanelData: {
            fileType,
            selectedBranchIds,
            includeMarkAsPaidChecked: isMarkAsPaidChecked
          }
        });
      }
      sendMixpanelEvent(Event.DOWNLOAD_GST_REPORT_CLICKED, {
        startDate,
        endDate,
        selectedBranchIds,
        includeMarkAsPaidChecked: isMarkAsPaidChecked
      });
      sendMixpanelEventV2(MixpanelEventV2.REPORT_DOWNLOADED, {
        report_type: 'GST Report',
        date_range: { start_date: startDate, end_date: endDate },
        branch_name: branchNames.join(', '),
        include_mark_as_paid: isMarkAsPaidChecked
      });
    } else if ('error' in res) {
      const error = res.error as ApiError;
      const message =
        error?.status === 426
          ? 'subscription plan has expired'
          : 'Error while downloading report';
      dispatch(
        updateDownloadStatus({
          id: downloadId,
          status: 'error',
          error: message
        })
      );
      createErrorAlert(message);
      if (error?.status === 426) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        }
      }
      sendMixpanelEvent(Event.DOWNLOAD_GST_REPORT_CLICKED, {
        error: message
      });
    }
  };

  const handleDownloadCustomReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    isQuickCollectChecked: boolean,
    selectCategory: string,
    branchNames: string[] = [],
    selectedGroups: SelectedItemsObj = {},
    allGroupsByBranch: SelectedItemsObj = {},
    customFields: string[] = [],
    globalFields: string[] = [],
    reportType: 'branch' | 'organisation' = 'branch'
  ) => {
    const { startDate, endDate } = formatDateRange(selectedDateRange);

    if (isTrialMode) {
      const downloadId = `custom-report-${Date.now()}`;
      dispatch(
        addDownload({
          id: downloadId,
          fileName: downloadId,
          fileType: 'xlsx'
        })
      );
      try {
        const { generateTrialCustomReport } = await import(
          '@features/trial-mode/mock-data/reports'
        );
        const blob = await generateTrialCustomReport(
          TRIAL_MOCK_IDS.ORG_NAME,
          startDate,
          endDate
        );
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(now.getDate()).padStart(2, '0')}_${String(
          now.getHours()
        ).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(
          now.getSeconds()
        ).padStart(2, '0')}`;
        await downloadFileFromUrlUsingBlob(url, `Custom_Report_${ts}.xlsx`);
        URL.revokeObjectURL(url);
        dispatch(updateDownloadStatus({ id: downloadId, status: 'completed' }));
      } catch {
        dispatch(
          updateDownloadStatus({
            id: downloadId,
            status: 'error',
            error: 'Failed to generate report'
          })
        );
      }
      return;
    }

    const requestBody = buildCustomReportV3Body({
      fromTimestamp: formattedDate(startDate),
      toTimestamp: formattedDate(endDate, true),
      selectedBranchIds,
      selectedGroups,
      allGroupsByBranch,
      includeMarkAsPaid: true,
      includeQuickCollect: isQuickCollectChecked,
      includeTaxInfo: false,
      category: selectCategory,
      paymentMode: '',
      customFields,
      globalFields,
      reportType
    });

    const downloadId = `custom-report-${Date.now()}`;

    const res = await getCustomReportJobIdV3({
      params: {
        organizationId,
        branchId: defaultBranchId || selectedBranchIds[0] || ''
      },
      body: requestBody
    });
    if ('data' in res) {
      const jobId = res.data?.job_id;
      if (jobId) {
        dispatch(
          addDownload({
            id: downloadId,
            fileName: downloadId,
            fileType: 'xlsx'
          })
        );

        const fileType = 'custom_report';

        await pollDownloadStatus({
          jobId,
          downloadId,
          mixpanelData: {
            fileType,
            startDate,
            endDate,
            selectedBranchIds,
            includeMarkAsPaidChecked: true
          }
        });
      }
      sendMixpanelEvent(Event.DOWNLOAD_CUSTOM_REPORT_CLICKED, {
        startDate,
        endDate,
        selectedBranchIds,
        includeMarkAsPaidChecked: true,
        isQuickCollectChecked,
        categorySelected: selectCategory
      });
      sendMixpanelEventV2(MixpanelEventV2.REPORT_DOWNLOADED, {
        report_type: 'Collections Report',
        date_range: { start_date: startDate, end_date: endDate },
        branch_name: branchNames.join(', '),
        include_mark_as_paid: true,
        include_quick_collect: isQuickCollectChecked,
        filter_by_category: selectCategory
      });
    } else if ('error' in res) {
      const error = res.error as ApiError;
      const message =
        error?.status === 426
          ? 'subscription plan has expired'
          : 'Error while downloading report';
      createErrorAlert(message);
      dispatch(
        updateDownloadStatus({
          id: downloadId,
          status: 'error',
          error: message
        })
      );
      if (error?.status === 426) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        }
      }
      sendMixpanelEvent(Event.DOWNLOAD_CUSTOM_REPORT_ERROR, {
        error: message
      });
    }
  };

  const handleDownloadChargesReport = async (
    selectedDateRange: DateRange,
    selectedBranchIds: string[],
    defaultBranchId: string,
    branchNames: string[] = []
  ) => {
    const { startDate, endDate } = formatDateRange(selectedDateRange);

    const queryObject = {
      from_timestamp: formattedDate(startDate),
      to_timestamp: formattedDate(endDate, true),
      branch_id: selectedBranchIds.join(',')
    };
    const queryString = buildQueryString(queryObject);

    const downloadId = `charges-report-${Date.now()}`;

    const res = await getChargesReportJobId({
      params: {
        organizationId,
        branchId:
          selectedBranchIds.length === 1
            ? selectedBranchIds[0]
            : defaultBranchId
      },
      queryString
    });

    if (res?.data) {
      const jobId = res.data?.job_id;
      if (jobId) {
        dispatch(
          addDownload({
            id: downloadId,
            fileName: downloadId,
            fileType: 'xlsx'
          })
        );

        const fileType = 'charges_report';

        await pollDownloadStatus({
          jobId,
          downloadId,
          mixpanelData: {
            fileType,
            startDate,
            endDate,
            selectedBranchIds
          }
        });
      }
      sendMixpanelEvent(Event.DOWNLOAD_CHARGES_REPORT_CLICKED, {
        startDate,
        endDate,
        selectedBranchIds
      });
      sendMixpanelEventV2(MixpanelEventV2.REPORT_DOWNLOADED, {
        report_type: 'Transaction Charges Report',
        date_range: { start_date: startDate, end_date: endDate },
        branch_name: branchNames.join(', ')
      });
    } else if ('error' in res) {
      const error = res.error as ApiError;
      const message =
        error?.status === 426
          ? 'subscription plan has expired'
          : 'Error while downloading report';
      createErrorAlert(message);
      dispatch(
        updateDownloadStatus({
          id: downloadId,
          status: 'error',
          error: message
        })
      );
      if (error?.status === 426) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        }
      }
      sendMixpanelEvent(Event.DOWNLOAD_CHARGES_REPORT_ERROR, {
        error: message
      });
    }
  };

  const handleDownloadLeadReport = async (
    orgId: string,
    payload: GenerateReportRequest
  ) => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const downloadId = `${payload.report_type}-${Date.now()}`;
      const result = await getReportJobStatus({
        organizationId: orgId,
        payload
      });
      if ('data' in result && result.data) {
        const jobId = result.data?.job_id;
        if (jobId) {
          dispatch(
            addDownload({
              id: downloadId,
              fileName: downloadId,
              fileType: 'xlsx'
            })
          );

          await pollLeadReportJobStatus({
            jobId,
            downloadId
          });
        }
      }
    }
  };

  return {
    handleDownloadGstReport,
    handleDownloadCustomReport,
    handleDownloadChargesReport,
    handleDownloadLeadReport
  };
};

export default useDownloadReport;
